"use client";

import React, { createContext, Suspense } from "react";
import { useSearchParams } from "next/navigation";

import {
  REACT_NATIVE_COOKIE,
  REACT_NATIVE_QUERY_PARAM,
  getCookieValue,
  isEnabledReactNativeValue
} from "@/utils/reactnative/reactNative";

export const ReactNativeAppContext = createContext(false);

type ReactNativeAppProviderProps = {
  children: React.ReactNode;
  isReactNativeApp?: boolean;
};

function resolveIsReactNativeApp(
  queryValue: string | null,
  initialIsReactNativeApp: boolean
) {
  if (queryValue !== null) {
    return isEnabledReactNativeValue(queryValue);
  }

  if (typeof document !== "undefined") {
    const cookieValue = getCookieValue(document.cookie, REACT_NATIVE_COOKIE);
    if (cookieValue !== null) {
      return cookieValue === "true";
    }
  }

  return initialIsReactNativeApp;
}

const ReactNativeAppProviderInner = ({
  children,
  isReactNativeApp: initialIsReactNativeApp = false
}: ReactNativeAppProviderProps) => {
  const searchParams = useSearchParams();
  const queryValue = searchParams.get(REACT_NATIVE_QUERY_PARAM);
  const isReactNativeApp = resolveIsReactNativeApp(
    queryValue,
    initialIsReactNativeApp
  );

  return (
    <ReactNativeAppContext.Provider value={isReactNativeApp}>
      {children}
    </ReactNativeAppContext.Provider>
  );
};

const ReactNativeAppProvider = ({
  children,
  isReactNativeApp = false
}: ReactNativeAppProviderProps) => {
  return (
    <Suspense
      fallback={
        <ReactNativeAppContext.Provider value={isReactNativeApp}>
          {children}
        </ReactNativeAppContext.Provider>
      }
    >
      <ReactNativeAppProviderInner isReactNativeApp={isReactNativeApp}>
        {children}
      </ReactNativeAppProviderInner>
    </Suspense>
  );
};

export default ReactNativeAppProvider;
